import React, { useState, useEffect } from 'react';
import { getPathParam, navigate } from '../utils/navigation';
import apiService from '../services/apiService';
import TransactionLog from '../components/TransactionLog';
import Layout from '../components/Layout';

const PrenupDetails = () => {
  const id = getPathParam();
  
  const [prenup, setPrenup] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showTransactionLog, setShowTransactionLog] = useState(false);

  const loadPrenupDetails = React.useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await apiService.getPrenupById(id);
      
      if (response.success) {
        setPrenup(response.data);
      } else {
        setError('لم يتم العثور على الاستمارة');
      }
    } catch (error) {
      console.error('Error loading prenup details:', error);
      setError('حدث خطأ في تحميل تفاصيل الاستمارة');
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      loadPrenupDetails();
    }
  }, [id, loadPrenupDetails]);

  const handleDelete = async () => {
    if (window.confirm('هل أنت متأكد من حذف هذه الاستمارة؟')) {
      try {
        const response = await apiService.deletePrenup(id);
        if (response.success) {
          alert('تم حذف الاستمارة بنجاح');
          navigate('/my-prenups');
        } else {
          alert('حدث خطأ في حذف الاستمارة');
        }
      } catch (error) {
        console.error('Delete error:', error);
        alert('حدث خطأ في حذف الاستمارة');
      }
    }
  };

  const handleEdit = () => {
    navigate(`/create-prenup?edit=${id}`);
  };

  const handlePrint = () => {
    if (prenup.status && prenup.status !== 'approved' && prenup.status !== 'printed') {
      alert('يمكن طباعة الشهادة للاستمارات المعتمدة فقط');
      return;
    }

    // Navigate to print page with prefilled data
    navigate(`/print?nationalId=${prenup.NID || prenup.PASSPORTNUMBER}`);
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="container mt-5">
          <div className="row justify-content-center">
            <div className="col-auto text-center">
              <div className="spinner-border text-primary mb-3" role="status">
                <span className="sr-only">جاري التحميل...</span>
              </div>
              <h5>جاري تحميل البيانات...</h5>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="container mt-5">
          <div className="alert alert-danger text-center">
            {error}
          </div>
          <div className="text-center">
            <button
              className="btn btn-secondary"
              onClick={() => navigate('/my-prenups')}
            >
              العودة إلى استماراتي
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  if (!prenup) {
    return (
      <Layout>
        <div className="container mt-5">
          <div className="alert alert-warning text-center">
            لم يتم العثور على الاستمارة
          </div>
          <div className="text-center">
            <button
              className="btn btn-secondary"
              onClick={() => navigate('/my-prenups')}
            >
              العودة إلى استماراتي
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <span className="badge badge-success badge-lg">جاهز للطباعة</span>;
      case 'rejected':
        return <span className="badge badge-danger badge-lg">مرفوضة</span>;
      case 'sample_replacement':
        return <span className="badge badge-warning badge-lg">مطلوب إستبدال العينة</span>;
      case 'printed':
        return <span className="badge badge-info badge-lg">تم الطباعة</span>;
      case 'pending':
        return <span className="badge badge-secondary badge-lg">منتظر نتيجة الHVD</span>;
      default:
        return <span className="badge badge-success badge-lg">جاهز للطباعة</span>;
    }
  };

  return (
    <Layout>
      <div className="container mt-5">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2>تفاصيل الاستمارة</h2>
          <div>
          <button 
            className="btn btn-secondary mr-2"
            onClick={() => navigate('/my-prenups')}
          >
            العودة إلى استماراتي
          </button>
          {(prenup.status === 'pending' || prenup.status === 'sample_replacement' || !prenup.status) && (
            <button 
              className="btn btn-warning mr-2"
              onClick={handleEdit}
            >
              <>تعديل</>
            </button>
          )}
          {(!prenup.status || prenup.status === 'approved' || prenup.status === 'printed') && (
            <button 
              className="btn btn-success mr-2"
              onClick={handlePrint}
            >
              طباعة الشهادة
            </button>
          )}
          <button 
            className="btn btn-info mr-2"
            onClick={() => setShowTransactionLog(true)}
          >
            سجل المعاملات
          </button>
          <button 
            className="btn btn-danger"
            onClick={handleDelete}
          >
            <>حذف</>
          </button>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">معلومات الاستمارة</h5>
                {getStatusBadge(prenup.status)}
              </div>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-6">
                  <table className="table table-borderless">
                    <tbody>
                      <tr>
                        <td><strong>رقم الوثيقة:</strong></td>
                        <td>{prenup.documentNumber || 'غير محدد'}</td>
                      </tr>
                      <tr>
                        <td><strong>الاسم الكامل:</strong></td>
                        <td>{prenup.fullName || `${prenup.FIRSTNAME} ${prenup.SECONDNAME} ${prenup.THIRDNAME || ''} ${prenup.FOURTHNAME || ''}`.trim()}</td>
                      </tr>
                      <tr>
                        <td><strong>الرقم القومي:</strong></td>
                        <td>{prenup.NID || prenup.PASSPORTNUMBER || 'غير محدد'}</td>
                      </tr>
                      <tr>
                        <td><strong>تاريخ الميلاد:</strong></td>
                        <td>{prenup.BIRTHDATE ? new Date(prenup.BIRTHDATE).toLocaleDateString('ar-EG') : 'غير محدد'}</td>
                      </tr>
                      <tr>
                        <td><strong>النوع:</strong></td>
                        <td>{prenup.GENDER === 'M' ? 'ذكر' : prenup.GENDER === 'F' ? 'أنثى' : 'غير محدد'}</td>
                      </tr>
                      <tr>
                        <td><strong>رقم الهاتف:</strong></td>
                        <td>{prenup.PHONE || 'غير محدد'}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className="col-md-6">
                  <table className="table table-borderless">
                    <tbody>
                      <tr>
                        <td><strong>الجنسية:</strong></td>
                        <td>{prenup.NATIONALITY === 'E' ? 'مصري' : prenup.NATIONALITY === 'N' ? 'غير مصري' : 'غير محدد'}</td>
                      </tr>
                      <tr>
                        <td><strong>رقم جواز السفر:</strong></td>
                        <td>{prenup.PASSPORTNUMBER || 'غير محدد'}</td>
                      </tr>
                      <tr>
                        <td><strong>العنوان:</strong></td>
                        <td>{prenup.ADDRESS || 'غير محدد'}</td>
                      </tr>
                      <tr>
                        <td><strong>تاريخ الإنشاء:</strong></td>
                        <td>{new Date(prenup.createdAt).toLocaleDateString('ar-EG')}</td>
                      </tr>
                      <tr>
                        <td><strong>آخر تحديث:</strong></td>
                        <td>{new Date(prenup.updatedAt).toLocaleDateString('ar-EG')}</td>
                      </tr>
                      <tr>
                        <td><strong>الحالة:</strong></td>
                        <td>{getStatusBadge(prenup.status)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Partner Information Section */}
      {(prenup.PARTNERNAME || prenup.PARTNERNID) && (
        <div className="row mt-4">
          <div className="col-12">
            <div className="card">
              <div className="card-header">
                <h5 className="mb-0">معلومات الشريك</h5>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-6">
                    <table className="table table-borderless">
                      <tbody>
                        <tr>
                          <td><strong>اسم الشريك:</strong></td>
                          <td>{prenup.PARTNERNAME || 'غير محدد'}</td>
                        </tr>
                        <tr>
                          <td><strong>رقم القومي/جواز السفر:</strong></td>
                          <td>{prenup.PARTNERNID || prenup.PARTNERPASSPORTNUMBER || 'غير محدد'}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="col-md-6">
                    <table className="table table-borderless">
                      <tbody>
                        <tr>
                          <td><strong>الجنسية:</strong></td>
                          <td>{prenup.PARTNERNATIONALITY === 'E' ? 'مصري' : prenup.PARTNERNATIONALITY === 'N' ? 'غير مصري' : 'غير محدد'}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Medical Information Section */}
      <div className="row mt-4">
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">المعلومات الطبية</h5>
            </div>
            <div className="card-body">
              {/* Basic Medical Info */}
              <div className="row mb-4">
                <div className="col-md-4">
                  <p><strong>الطول:</strong> {prenup.HEIGHT || '-'} سم</p>
                </div>
                <div className="col-md-4">
                  <p><strong>الوزن:</strong> {prenup.WEIGHT || '-'} كجم</p>
                </div>
                <div className="col-md-4">
                  <p><strong>BMI:</strong> {prenup.HEIGHT && prenup.WEIGHT ? (prenup.WEIGHT / Math.pow(prenup.HEIGHT / 100, 2)).toFixed(1) : '-'}</p>
                </div>
              </div>
              
              <div className="row mb-4">
                <div className="col-md-4">
                  <p><strong>فصيلة الدم:</strong> {prenup.bloodTypeEn || prenup.bloodType || '-'}</p>
                </div>
                <div className="col-md-4">
                  <p><strong>RH:</strong> {prenup.RH === 'Y' ? 'إيجابي' : prenup.RH === 'N' ? 'سلبي' : '-'}</p>
                </div>
                <div className="col-md-4">
                  <p><strong>الهيموجلوبين:</strong> {prenup.HB || '-'} جم/دل</p>
                </div>
              </div>
              
              <div className="row mb-4">
                <div className="col-md-4">
                  <p><strong>ضغط الدم:</strong> {prenup.BLOODPRESSURESYSTOLIC && prenup.BLOODPRESSUREDIASTOLIC ? `${prenup.BLOODPRESSURESYSTOLIC}/${prenup.BLOODPRESSUREDIASTOLIC}` : '-'}</p>
                </div>
                <div className="col-md-4">
                  <p><strong>السكر العشوائي:</strong> {prenup.RANDOMGLUCOSEMEASURE || '-'} مجم/دل</p>
                </div>
              </div>
              
              {/* Lab Tests */}
              <h6 className="mb-3">نتائج الفحوصات المعملية:</h6>
              <div className="row mb-4">
                <div className="col-md-4">
                  <p><strong>HBsAg:</strong> {prenup.HBSAG === 'Y' ? 'متفاعل' : prenup.HBSAG === 'N' ? 'غير متفاعل' : '-'}</p>
                </div>
                <div className="col-md-4">
                  <p><strong>Anti-HIV:</strong> {prenup.ANTIHIV === 'Y' ? 'متفاعل' : prenup.ANTIHIV === 'N' ? 'غير متفاعل' : '-'}</p>
                </div>
                <div className="col-md-4">
                  <p><strong>Anti-HCV:</strong> {prenup.ANTIHCV === 'Y' ? 'متفاعل' : prenup.ANTIHCV === 'N' ? 'غير متفاعل' : '-'}</p>
                </div>
              </div>
              
              {/* HbElectrophoresis Section */}
              <h6 className="mb-3">Hb Electrophoresis:</h6>
              <div className="row">
                <div className="col">
                  <div className="table-responsive">
                    <table className="table table-bordered text-center">
                      <thead>
                        <tr>
                          <th>A</th>
                          <th>A2</th>
                          <th>F</th>
                          <th>C</th>
                          <th>S</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>
                            <strong>{prenup.HBELECA || '0'}%</strong>
                            <br/>
                            <small className="text-muted">{prenup.HBELECASTAT || 'Normal'}</small>
                          </td>
                          <td>
                            <strong>{prenup.HBELECA2 || '0'}%</strong>
                            <br/>
                            <small className="text-muted">{prenup.HBELECA2STAT || 'Normal'}</small>
                          </td>
                          <td>
                            <strong>{prenup.HBELECF || '0'}%</strong>
                            <br/>
                            <small className="text-muted">{prenup.HBELECFSTAT || 'Normal'}</small>
                          </td>
                          <td>
                            <strong>{prenup.HBELECC || '0'}%</strong>
                            <br/>
                            <small className="text-muted">{prenup.HBELECCSTAT || 'Normal'}</small>
                          </td>
                          <td>
                            <strong>{prenup.HBELECS || '0'}%</strong>
                            <br/>
                            <small className="text-muted">{prenup.HBELECSSTAT || 'Normal'}</small>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Additional Medical Info */}
              <h6 className="mb-3 mt-4">معلومات إضافية:</h6>
              <div className="row mb-4">
                <div className="col-md-4">
                  <p><strong>باركود الفلتر:</strong> {prenup.HVDBARCODE || '-'}</p>
                </div>
                <div className="col-md-4">
                  <p><strong>مريض سكر:</strong> {prenup.DIABETIC === 'Y' ? 'نعم' : prenup.DIABETIC === 'N' ? 'لا' : '-'}</p>
                </div>
                <div className="col-md-4">
                  <p><strong>مريض ضغط:</strong> {prenup.BLDPRESSURE === 'Y' ? 'نعم' : prenup.BLDPRESSURE === 'N' ? 'لا' : '-'}</p>
                </div>
              </div>
              
              <div className="row mb-4">
                <div className="col-md-4">
                  <p><strong>صلة القرابة:</strong> {prenup.ISRELATEDQUES === 'Y' ? 'نعم' : prenup.ISRELATEDQUES === 'N' ? 'لا' : '-'}</p>
                </div>
                <div className="col-md-4">
                  <p><strong>الزواج السابق:</strong> {prenup.PREVMARRIAGEQUES === 'Y' ? 'نعم' : prenup.PREVMARRIAGEQUES === 'N' ? 'لا' : '-'}</p>
                </div>
                <div className="col-md-4">
                  <p><strong>نقل الدم:</strong> {prenup.BLOODTRANSFER === '1' ? 'نعم' : prenup.BLOODTRANSFER === '0' ? 'لا' : '-'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Comments Section */}
      {prenup.comments && (
        <div className="row mt-4">
          <div className="col-12">
            <div className="card">
              <div className="card-header">
                <h5 className="mb-0">التعليقات</h5>
              </div>
              <div className="card-body">
                <p>{prenup.comments}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Transaction Log Modal */}
      {showTransactionLog && (
        <TransactionLog
          prenupId={prenup._id}
          documentNumber={prenup.documentNumber}
          onClose={() => setShowTransactionLog(false)}
        />
      )}
      </div>
    </Layout>
  );
};

export default PrenupDetails;
